/** Admin · audit log (GET /admin/audit, cursor-paged) — every sensitive action, append-only. */
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";

interface AuditEntry {
  id: string;
  ts: string;
  actorUserId: string | null;
  actorName: string | null;
  orgId: string | null;
  action: string;
  target: string | null;
  dataCategories: string[];
}

interface AuditResponse {
  entries: AuditEntry[];
  nextBefore: string | null;
}

/** Pill tone by action keyword — impersonation violet, secret/MFA warn, flags/releases teal, grants green. */
function actionPill(action: string): string {
  const a = action.toLowerCase();
  if (a.includes("impersonat")) return "pill r";
  if (a.includes("secret") || a.includes("mfa") || a.includes("rotat") || a.includes("payment")) return "pill r";
  if (a.includes("flag") || a.includes("release") || a.includes("model") || a.includes("region")) return "pill n";
  if (a.includes("grant") || a.includes("provision")) return "pill v";
  return "pill n";
}

export function Audit(): ReactNode {
  // A stack of `before` cursors: empty = newest page; push to go older, pop to come back.
  const [cursors, setCursors] = useState<string[]>([]);
  const before = cursors.length > 0 ? cursors[cursors.length - 1] : null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-audit", before],
    queryFn: () =>
      api.get<AuditResponse>(`/admin/audit${before ? `?before=${encodeURIComponent(before)}` : ""}`),
  });
  const entries = data?.entries ?? [];

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Audit log</h1>
          <div className="sub">Every sensitive action — append-only · live from the API</div>
        </div>
        <span className="pill n">Security</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading audit trail…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load the audit log. Please retry in a moment.</span>
        </div>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <div className="card pad">
          <p className="mut sm" style={{ margin: 0 }}>
            Nothing here yet — sensitive actions will appear as they happen.
          </p>
        </div>
      )}

      {!isLoading && !isError && entries.length > 0 && (
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Data categories</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="mut sm tnum" style={{ whiteSpace: "nowrap" }}>
                      {new Date(e.ts).toLocaleString()}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {e.actorName ?? (e.actorUserId ? `${e.actorUserId.slice(0, 8)}…` : "system")}
                    </td>
                    <td>
                      <span className={actionPill(e.action)} style={{ textTransform: "capitalize" }}>
                        {e.action.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="mut sm">{e.target ?? "—"}</td>
                    <td>
                      {e.dataCategories.length === 0 && <span className="mut sm">—</span>}
                      {e.dataCategories.map((c) => (
                        <span key={c} className="tag" style={{ marginRight: 6 }}>
                          {c}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button
          className="btn sm ghost"
          disabled={cursors.length === 0}
          onClick={() => setCursors((c) => c.slice(0, -1))}
        >
          ← Newer
        </button>
        <button
          className="btn sm ghost"
          disabled={!data?.nextBefore}
          onClick={() => {
            const next = data?.nextBefore;
            if (next) setCursors((c) => [...c, next]);
          }}
        >
          Older →
        </button>
      </div>
    </section>
  );
}
